import React, { useMemo, useState } from 'react'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'
import { OKREGI_RO, okregRo } from '../../lib/regionyRumunii'
import MapaPolski from '../wykonawcy/MapaPolski'
import { REGIONY } from '../wykonawcy/regionyPolski'
import { ORIENTACJE, ZACIENIENIA, PROFILE } from '../../lib/mocKalkulator'
import { prognoza } from '../../lib/prognozaProdukcji'
import { zapiszDaneProjektu } from '../../lib/daneProjektu'
import WykresProdukcji from './WykresProdukcji'

/**
 * Prognoza produkcji dla dobranego zestawu.
 *
 * DLACZEGO PYTAMY O WOJEWÓDZTWO: między pomorskim a dolnośląskim jest ok. 10% różnicy
 * w rocznym uzysku (987 vs 1092 kWh/kWp wg PVGIS). Bez tego pytania ta różnica weszłaby
 * do wyniku jako cicha nieprawda — a dziesięć procent produkcji to przy zwrocie
 * inwestycji blisko rok.
 *
 * Pozostałe pytania są DOKŁADNIE te same, co w kalkulatorze mocy na stronie głównej,
 * i kto przez niego przeszedł, ma je już wypełnione (patrz lib/daneProjektu).
 */

/**
 * Klucz miejsca do prognozy. W Polsce województwo (domyślnie mazowieckie), poza
 * Polską okręg z lib/regionyRumunii zapisany w TYM SAMYM polu `wojewodztwo`
 * (domyślnie București). Klucz spoza listy okręgów, np. województwo zapisane
 * wcześniej na polskiej wersji, traktujemy jak brak wyboru.
 */
export const regionPrognozy = (klucz) => {
  if (jestPolska) return klucz || 'mazowieckie'
  return okregRo(klucz) ? klucz : 'ro_bucuresti'
}

const nazwaRegionu = (klucz) => (jestPolska
  ? REGIONY.find((r) => r.key === klucz)?.nazwa || ''
  : okregRo(regionPrognozy(klucz))?.nazwa || '')

/** Etykieta opcji z lib/mocKalkulator w języku wersji (słownik `projekt`). */
const etykietaOpcji = (t, grupa, opcja) => (opcja
  ? t(`opcje.${grupa}.${opcja.key}`, { defaultValue: opcja.label })
  : '')

const Kafelek = ({ etykieta, wartosc, opis, mocny }) => (
  <div className={`rounded-xl border px-3 py-2.5 ${
    mocny ? 'border-blue-200 bg-blue-50/60' : 'border-gray-200 bg-white'}`}>
    <p className="text-[11px] text-gray-500">{etykieta}</p>
    <p className={`text-lg font-bold tabular-nums leading-tight
                   ${mocny ? 'text-blue-900' : 'text-gray-900'}`}>{wartosc}</p>
    {opis && <p className="text-[11px] text-gray-400">{opis}</p>}
  </div>
)

const Wybor = ({ etykieta, wartosc, opcje, onZmiana, grupa }) => {
  const { t } = useTranslation('projekt')
  return (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{etykieta}</span>
    <select value={wartosc} onChange={(e) => onZmiana(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500">
      {opcje.map((o) => <option key={o.key} value={o.key}>{etykietaOpcji(t, grupa, o)}</option>)}
    </select>
  </label>
  )
}

const SekcjaProdukcji = ({
  dane, onZmiana, kwp, pojemnoscMagazynuKwh, naWydruk = false, bezNaglowka = false,
  // Prognoza policzona wyżej wspólnym silnikiem (`lib/ekonomiaKlienta.js`), gdy wołający ją ma:
  // wtedy strona produkcji pokazuje dokładnie te liczby, z których liczy się zwrot.
  prognoza: prognozaPodana = null,
  // Przy kliencie: zmiana trafia do jego karty (`onEdycja`), nie do sessionStorage (patrz `ModalSchematu`).
  onEdycja = null,
  // Produkcja z Projektu 3D (prawdziwy dach i cień): lokalizacja, ustawienie i zacienienie niczego nie zmieniają,
  // więc ich nie pytamy.
  produkcjaZ3D = false,
}) => {
  const { t } = useTranslation('projekt')
  const [mapaOtwarta, setMapaOtwarta] = useState(!dane.wojewodztwo)

  const ustaw = (czesc) => (onEdycja ? onEdycja(czesc) : onZmiana(zapiszDaneProjektu(czesc)))

  const policzona = useMemo(() => (prognozaPodana ? null : prognoza({
    kwp,
    wojewodztwo: regionPrognozy(dane.wojewodztwo),
    ustawienie: dane.ustawienie,
    zacienienie: dane.zacienienie,
    zuzycieRoczne: Number(dane.zuzycieRoczne) || 0,
    profil: dane.profil,
    dodatki: dane.dodatki,
    pojemnoscMagazynuKwh: pojemnoscMagazynuKwh || 0,
  })), [dane, kwp, pojemnoscMagazynuKwh, prognozaPodana])
  const wynik = prognozaPodana || policzona

  const r = wynik.rocznie
  const zn = (v) => Math.round(v).toLocaleString(KRAJ.locale)

  return (
    <section className="space-y-4">
      {/* Wewnątrz strony dokumentu nagłówek pochodzi ze strony — ten dublowałby
          jej tytuł dwa centymetry niżej. Na ekranie, w zakładce, zostaje. */}
      {!bezNaglowka && (
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-bold text-gray-900">{t('produkcja.tytul')}</h3>
          <p className="text-xs text-gray-400">
            {t('produkcja.naglowek_opis', { kwp, uzysk: wynik.uzyskJednostkowy })}
          </p>
        </div>
      )}

      {/* Lokalizacja. Poza Polską zwykła lista okręgów zamiast mapy województw:
          42 pozycje mieszczą się na liście rozwijanej (patrz lib/regionyRumunii). */}
      {produkcjaZ3D ? null : !jestPolska ? (
        <label className="block print:hidden">
          <span className="block text-xs font-medium text-gray-600 mb-1">{t('produkcja.okreg')}</span>
          <select value={regionPrognozy(dane.wojewodztwo)}
                  onChange={(e) => ustaw({ wojewodztwo: e.target.value })}
                  className="w-full sm:w-64 px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white
                             focus:outline-none focus:ring-2 focus:ring-blue-500">
            {OKREGI_RO.map((o) => <option key={o.key} value={o.key}>{o.nazwa}</option>)}
          </select>
        </label>
      ) : (
      <div className="print:hidden">
        {dane.wojewodztwo && !mapaOtwarta ? (
          <button type="button" onClick={() => setMapaOtwarta(true)}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-800">
            <MapPinIcon className="w-4 h-4" />
            {nazwaRegionu(dane.wojewodztwo)}
            <span className="text-xs text-gray-400">{t('produkcja.zmien')}</span>
          </button>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <p className="text-xs font-medium text-gray-600 mb-2">
              {t('produkcja.gdzie')}
            </p>
            <MapaPolski
              wybrany={dane.wojewodztwo}
              onWybierz={(k) => {
                if (!k) return
                ustaw({ wojewodztwo: k })
                setMapaOtwarta(false)
              }}
              className="max-w-[340px] mx-auto"
            />
          </div>
        )}
      </div>
      )}

      <div className={`grid ${produkcjaZ3D ? 'sm:grid-cols-1' : 'sm:grid-cols-3'} gap-3 print:hidden`}>
        {!produkcjaZ3D && (
          <>
            <Wybor etykieta={t('produkcja.ustawienie')} wartosc={dane.ustawienie} grupa="orientacje"
                   opcje={ORIENTACJE} onZmiana={(v) => ustaw({ ustawienie: v })} />
            <Wybor etykieta={t('produkcja.zacienienie')} wartosc={dane.zacienienie} grupa="zacienienia"
                   opcje={ZACIENIENIA} onZmiana={(v) => ustaw({ zacienienie: v })} />
          </>
        )}
        <Wybor etykieta={t('produkcja.profil')} wartosc={dane.profil} grupa="profile"
               opcje={PROFILE} onZmiana={(v) => ustaw({ profil: v })} />
      </div>

      <label className="block print:hidden">
        <span className="block text-xs font-medium text-gray-600 mb-1">
          {t('produkcja.zuzycie_roczne')}
          <span className="text-gray-400">{t('produkcja.zuzycie_roczne_dopisek')}</span>
        </span>
        <input type="number" min="0" step="100" inputMode="numeric"
               value={dane.zuzycieRoczne || ''}
               onChange={(e) => ustaw({ zuzycieRoczne: Number(e.target.value) || 0 })}
               placeholder={t('produkcja.zuzycie_placeholder')}
               className="w-full sm:w-56 px-3 py-2 rounded-lg border border-gray-200 text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </label>

      <WykresProdukcji miesiace={wynik.miesiace} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kafelek etykieta={t('produkcja.produkcja_roczna')} wartosc={`${zn(r.produkcjaKwh)} kWh`} mocny />
        {r.autokonsumpcjaKwh != null ? (
          <>
            <Kafelek etykieta={t('produkcja.zuzyte_u_siebie')} wartosc={`${zn(r.autokonsumpcjaKwh)} kWh`}
                     opis={t('produkcja.procent_produkcji', { proc: r.autokonsumpcjaProc })} />
            <Kafelek etykieta={t('produkcja.oddane_do_sieci')} wartosc={`${zn(r.nadwyzkaKwh)} kWh`} />
            <Kafelek etykieta={t('produkcja.samowystarczalnosc')} wartosc={`${r.samowystarczalnoscProc}%`}
                     opis={t('produkcja.samowystarczalnosc_opis')} />
          </>
        ) : (
          <div className="col-span-2 sm:col-span-3 flex items-center text-xs text-gray-400 px-1">
            {t('produkcja.podaj_zuzycie')}
          </div>
        )}
      </div>

      {naWydruk && !produkcjaZ3D && (
        <p className="hidden print:block text-xs text-gray-500">
          {t('produkcja.wydruk', {
            lokalizacja: nazwaRegionu(dane.wojewodztwo) || t('produkcja.nie_podano'),
            ustawienie: etykietaOpcji(t, 'orientacje', ORIENTACJE.find((o) => o.key === dane.ustawienie)),
            zacienienie: etykietaOpcji(t, 'zacienienia', ZACIENIENIA.find((z) => z.key === dane.zacienienie))
              .toLowerCase(),
            zuzycie: zn(Number(dane.zuzycieRoczne) || 0),
          })}
        </p>
      )}
    </section>
  )
}

export default SekcjaProdukcji
